import { Vertical, VerticalConfig, VerticalConfigs } from 'utils/vertical';
import { LIVE_PERSON_ENGLISH_ONLY_SUPPORT_LOCALES } from 'providers/messaging/constants';
import {
  ARTISTS_SMARTLING_LOCALES,
  CONSUMER_SMARTLING_LOCALES,
  PODCASTERS_SMARTLING_LOCALES,
} from 'utils/i18n/localeConfig';
import { s4aLaunchedBool, s4pLaunchedBool } from 'utils/remote-config/properties';
import { LineOfBusiness } from 'providers/messaging/types';
import { isPillowTalkSupportedLanguageCode } from 'utils/type-guards';

const pillowTalkEnglishOnlySupportLocales = Object.values<string>(ARTISTS_SMARTLING_LOCALES).filter(
  value => !isPillowTalkSupportedLanguageCode(value),
);

// These configs might be available in frontend, so don't add anything sensitive like secrets etc
const verticalConfigs: VerticalConfigs = {
  [Vertical.ARTISTS]: {
    verticalName: Vertical.ARTISTS,
    pathParam: 'artists',
    cms: {
      dataset: process.env.VERTICAL_ARTIST_DATASET ?? 'artists_production',
      projectId: 'tsbk0zvv',
      apiVersion: '2021-10-21',
    },
    features: {
      communityLink: null,
      showHeaderGradient: false,
      configureMasthead: {
        header: 's4a',
        footer: 's4a',
      },
      configurePillowTalk: {
        lineOfBusiness: (process.env.NEXT_PUBLIC_VERTICAL_ARTIST_PILLOW_TALK_LOB as LineOfBusiness) ?? 'S4A',
        redirectUri: 'https://support.spotify.com/artists/contact-spotify-anonymous/',
      },
      emailMessagingFallback: false,
    },
    locales: {
      englishSupportOnly: pillowTalkEnglishOnlySupportLocales,
      smartling: ARTISTS_SMARTLING_LOCALES,
    },
    theme: 'encore-creator-light-theme',
    rolloutConfig: {
      rolloutProperty: s4aLaunchedBool,
      fallbackUrl: 'https://artists.spotify.com/help',
    },
  },
  [Vertical.CONSUMER]: {
    verticalName: Vertical.CONSUMER,
    pathParam: '',
    cms: {
      dataset: process.env.VERTICAL_CONSUMER_DATASET ?? 'production',
      projectId: 'tsbk0zvv',
      apiVersion: '2021-10-21',
    },
    features: {
      communityLink: 'https://community.spotify.com/t5/Spotify-Answers/tkb-p/Spotify-Answers',
      showHeaderGradient: true,
      configureMasthead: null,
      configurePillowTalk: null,
      emailMessagingFallback: true,
    },
    locales: {
      englishSupportOnly: LIVE_PERSON_ENGLISH_ONLY_SUPPORT_LOCALES,
      smartling: CONSUMER_SMARTLING_LOCALES,
    },
    theme: 'encore-dark-theme',
    rolloutConfig: null,
  },
  [Vertical.PODCASTERS]: {
    verticalName: Vertical.PODCASTERS,
    pathParam: 'podcasters',
    cms: {
      dataset: process.env.VERTICAL_PODCASTERS_DATASET ?? 'podcasters_production',
      projectId: 'tsbk0zvv',
      apiVersion: '2021-10-21',
    },
    features: {
      communityLink: null,
      showHeaderGradient: false,
      configureMasthead: null,
      configurePillowTalk: null,
      emailMessagingFallback: false,
    },
    locales: {
      englishSupportOnly: pillowTalkEnglishOnlySupportLocales,
      smartling: PODCASTERS_SMARTLING_LOCALES,
    },
    theme: 'encore-s4p-light-theme',
    rolloutConfig: {
      rolloutProperty: s4pLaunchedBool,
      fallbackUrl: 'https://help.spotifyforpodcasters.com/',
    },
  },
} as const;

export const getVerticalConfigs = (): VerticalConfigs => verticalConfigs;

export const getVerticalConfig = (vertical: Vertical): VerticalConfig => {
  return verticalConfigs[vertical];
};
